import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Stack,
  Button,
  TextField,
  Divider,
  Tooltip,
  CircularProgress
} from '@mui/material';
import UserAvatar from '../../components/Avatar/UserAvatar';
import {
  Forum as ForumIcon,
  PushPin as PushPinIcon,
  PushPinOutlined,
  ThumbUp,
  ThumbUpOffAlt,
  AddComment,
  CheckCircle,
  Send,
  Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../api/axios';
import ThreadCreateForm from '../../components/Forum/ThreadCreateForm';
import PageHeader from '../../components/Common/PageHeader';
import MarkdownRenderer from '../../components/Common/MarkdownRenderer';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

function ForumPage() {
  const navigate = useNavigate();
  const { user, selectedCourse } = useContext(AuthContext);

  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loadingThreadDetail, setLoadingThreadDetail] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);
  const [postingReply, setPostingReply] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newThread, setNewThread] = useState({ title: '', content: '', questionId: '', category: '' });
  const [questions, setQuestions] = useState([]);
  const [, setLoadingQuestions] = useState(false);
  const [, setQuestionCategory] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deletingReplyId, setDeletingReplyId] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  // const [deletingCommentId, setDeletingCommentId] = useState(null); // Reserved for future comment deletion feature

  // Use offeringId if available (for current term), otherwise fallback to templateId
  const courseId = selectedCourse?.offeringId || selectedCourse?.id;
  const courseTemplateId = selectedCourse?.id;

  const fetchQuestions = async () => {
    if (!courseTemplateId) return;
    try {
      setLoadingQuestions(true);
      const res = await axios.get('/questions', {
        params: { courseTemplateId }
      });
      setQuestions(res.data || []);
    } catch (error) {
      console.error('Failed to load questions', error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  useEffect(() => {
    if (!selectedCourse) {
      navigate('/courses');
      return;
    }
    fetchThreads();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse, navigate]);

  // Use MarkdownRenderer for content rendering (supports images, code blocks, formatting, etc.)
  const renderContent = (content) => {
    if (!content) return null;
    return <MarkdownRenderer content={content} />;
  };


  const fetchThreads = async () => {
    if (!courseId) {
      console.warn('ForumPage: No courseId, cannot fetch threads');
      return;
    }
    try {
      setLoadingThreads(true);
      console.log('ForumPage: Fetching threads for courseId:', courseId);
      const res = await axios.get(`/forum/course/${courseId}/threads`);
      console.log('ForumPage: Threads response:', res.data);
      setThreads(res.data || []);
      if (res.data?.length > 0) {
        const firstId = res.data[0].id;
        setSelectedThreadId(firstId);
        fetchThreadDetail(firstId);
      } else {
        setSelectedThreadId(null);
        setSelectedThread(null);
        setReplies([]);
      }
    } catch (error) {
      console.error('Failed to load threads', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoadingThreads(false);
    }
  };

  const fetchThreadDetail = async (threadId) => {
    try {
      setShowCreateForm(false); // leaving compose mode when viewing thread
      setLoadingThreadDetail(true);
      const res = await axios.get(`/forum/threads/${threadId}`);
      setSelectedThread(res.data.thread);
      setReplies(res.data.replies || []);
      setSelectedThreadId(threadId);
    } catch (error) {
      console.error('Failed to load thread detail', error);
    } finally {
      setLoadingThreadDetail(false);
    }
  };

  const handleCreateThread = async (formData) => {
    if (!formData.title.trim() || !formData.content.trim() || !formData.category) {
      return;
    }
    try {
      setCreatingThread(true);
      const res = await axios.post(`/forum/course/${courseId}/threads`, {
        title: formData.title.trim(),
        content: formData.content.trim(),
        questionId: formData.questionId || null,
        category: formData.category
      });
      setNewThread({ title: '', content: '', questionId: '', category: '' });
      setShowCreateForm(false);
      await fetchThreads();
      setSelectedThreadId(res.data.id);
      fetchThreadDetail(res.data.id);
    } catch (error) {
      console.error('Failed to create thread', error);
    } finally {
      setCreatingThread(false);
    }
  };

  const handleAddReply = async () => {
    if (!replyContent.trim() || !selectedThreadId) return;
    try {
      setPostingReply(true);
      await axios.post(`/forum/threads/${selectedThreadId}/replies`, {
        content: replyContent.trim()
      });
      setReplyContent('');
      fetchThreadDetail(selectedThreadId);
      fetchThreads(); // refresh reply counts
    } catch (error) {
      console.error('Failed to post reply', error);
    } finally {
      setPostingReply(false);
    }
  };

  const handleToggleLike = async (reply) => {
    try {
      const res = await axios.post(`/forum/replies/${reply.id}/like`);
      setReplies((prev) =>
        prev.map((r) =>
          r.id === reply.id ? { ...r, likes: res.data.likes, likedByMe: res.data.liked } : r
        )
      );
    } catch (error) {
      console.error('Failed to toggle like', error);
    }
  };

  const handleAcceptReply = async (reply) => {
    try {
      const res = await axios.post(`/forum/replies/${reply.id}/accept`, {
        accepted: !reply.acceptedByTeacher
      });
      setReplies((prev) =>
        prev.map((r) => (r.id === reply.id ? { ...r, ...res.data } : r))
      );
    } catch (error) {
      console.error('Failed to update acceptance', error);
    }
  };

  const handlePinThread = async () => {
    if (!selectedThread) return;
    try {
      const res = await axios.post(`/forum/threads/${selectedThread.id}/pin`, {
        pinned: !selectedThread.pinned
      });
      setSelectedThread(res.data);
      fetchThreads();
    } catch (error) {
      console.error('Failed to update pin', error);
    }
  };

  const handleDeleteThread = async () => {
    if (!selectedThread || deleting) return;
    if (!window.confirm('确定要删除这个讨论帖吗？该操作无法撤销。')) return;
    try {
      setDeleting(true);
      await axios.delete(`/forum/threads/${selectedThread.id}`);
      setSelectedThread(null);
      setSelectedThreadId(null);
      setReplies([]);
      fetchThreads();
    } catch (error) {
      console.error('Failed to delete thread', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteReply = async (reply) => {
    if (!reply || deletingReplyId) return;
    if (!window.confirm('确定要删除这条回答吗？该操作无法撤销。')) return;
    try {
      setDeletingReplyId(reply.id);
      await axios.delete(`/forum/replies/${reply.id}`);
      setReplies((prev) => prev.filter((r) => r.id !== reply.id));
      fetchThreads(); // update reply counts
    } catch (error) {
      console.error('Failed to delete reply', error);
    } finally {
      setDeletingReplyId(null);
    }
  };

  const handleAddComment = async (reply) => {
    const content = (commentInputs[reply.id] || '').trim();
    if (!content) return;
    try {
      const res = await axios.post(`/forum/replies/${reply.id}/comments`, { content });
      setReplies((prev) =>
        prev.map((r) =>
          r.id === reply.id ? { ...r, comments: [...(r.comments || []), res.data] } : r
        )
      );
      setCommentInputs((prev) => ({ ...prev, [reply.id]: '' }));
    } catch (error) {
      console.error('Failed to add comment', error);
    }
  };

  // handleDeleteComment 未使用，保留以备将来使用
  // const handleDeleteComment = async (reply, comment) => {
  //   if (!comment || deletingCommentId) return;
  //   if (!window.confirm('确定要删除这条评论吗？该操作无法撤销。')) return;
  //   try {
  //     setDeletingCommentId(comment.id);
  //     await axios.delete(`/forum/comments/${comment.id}`);
  //     setReplies((prev) =>
  //       prev.map((r) =>
  //         r.id === reply.id
  //           ? { ...r, comments: (r.comments || []).filter((c) => c.id !== comment.id) }
  //           : r
  //       )
  //     );
  //   } catch (error) {
  //     console.error('Failed to delete comment', error);
  //   } finally {
  //     setDeletingCommentId(null);
  //   }
  // };

  const selectedThreadReplies = useMemo(() => replies || [], [replies]);
  const hasAcceptedReply = useMemo(
    () => selectedThreadReplies.some((r) => r.acceptedByTeacher),
    [selectedThreadReplies]
  );
  const hasTeacherReply = useMemo(
    () => selectedThreadReplies.some((r) => r.authorRole === 'teacher'),
    [selectedThreadReplies]
  );
  const questionCategoryOptions = useMemo(() => {
    const cats = new Set(questions.map((q) => q.category));
    ['Other', '课程相关'].forEach((c) => cats.add(c));
    return Array.from(cats);
  }, [questions]);

  return (
    <Box sx={{ minHeight: '100vh', height: '100vh', overflow: 'hidden', bgcolor: 'transparent' }}>
      <PageHeader
        title={`课程论坛 · ${selectedCourse?.name || ''}${selectedCourse?.termCode ? ` (${selectedCourse.termCode})` : ''}`}
        backPath="/dashboard"
        icon={<ForumIcon color="primary" />}
        actions={
          <>
          <Button
            variant={showCreateForm ? 'contained' : 'outlined'}
            startIcon={<AddComment />}
            onClick={() => setShowCreateForm(true)}
          >
            发布问题
          </Button>
          {user && (
            <Stack direction="row" spacing={1} alignItems="center">
              <UserAvatar user={user} size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user.name} ({user.role === 'teacher' ? '教师' : '学生'})
              </Typography>
            </Stack>
          )}
          </>
        }
        sx={{ fontWeight: 700 }}
      />

      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', p: 2, gap: 2 }}>
        {/* Thread list */}
        <Box sx={{ width: { xs: '100%', md: '33.333%' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Paper variant="outlined" sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight="bold">讨论列表</Typography>
                <Chip label={`共 ${threads.length} 条`} size="small" />
              </Stack>
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              {loadingThreads ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : threads.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">还没有讨论，快来提问吧</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {threads.map((thread) => (
                    <ListItemButton
                      key={thread.id}
                      selected={thread.id === selectedThreadId}
                      onClick={() => fetchThreadDetail(thread.id)}
                      alignItems="flex-start"
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2" fontWeight="bold" noWrap>
                              {thread.title}
                            </Typography>
                            {thread.termCode && (
                              <Chip
                                size="small"
                                label={thread.termCode}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                                color="info"
                                variant="outlined"
                              />
                            )}
                            {thread.pinned && <Chip size="small" color="warning" label="置顶" icon={<PushPinIcon fontSize="small" />} />}
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {thread.content}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                              {thread.category && (
                                <Chip
                                  size="small"
                                  color="primary"
                                  label={thread.category}
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                              {thread.question && (
                                <Chip
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  label={`${thread.question.questionNumber} - ${thread.question.title}`}
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                              <Chip 
                                size="small" 
                                color={thread.isResolved ? 'success' : 'default'}
                                variant={thread.isResolved ? 'filled' : 'outlined'}
                                label={thread.isResolved ? '已解决' : '未解决'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              <Chip label={`${thread.replyCount} 回复`} size="small" variant="outlined" />
                              <Typography variant="caption" color="text.secondary">
                                {dayjs(thread.lastActivity || thread.createdAt).fromNow()}
                              </Typography>
                            </Stack>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Main panel */}
        <Box sx={{ width: { xs: '100%', md: '66.667%' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {showCreateForm ? (
              <ThreadCreateForm
                questions={questions}
                questionCategoryOptions={questionCategoryOptions}
                onSubmit={handleCreateThread}
                onCancel={() => {
                  setShowCreateForm(false);
                  setNewThread({ title: '', content: '', questionId: '', category: '' });
                  setQuestionCategory('');
                }}
                submitting={creatingThread}
                initialValues={newThread}
              />
            ) : (
              <Paper variant="outlined" sx={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {loadingThreadDetail ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                  </Box>
                ) : !selectedThread ? (
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', flexGrow: 1 }}>
                    <Typography variant="body1">选择左侧讨论查看详情</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack spacing={1} sx={{ flexGrow: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6" fontWeight="bold">
                              {selectedThread.title}
                            </Typography>
                            {selectedThread.termCode && (
                              <Chip
                                size="small"
                                label={selectedThread.termCode}
                                color="info"
                                variant="outlined"
                              />
                            )}
                            {selectedThread.category && (
                              <Chip
                                size="small"
                                color="primary"
                                label={selectedThread.category}
                              />
                            )}
                            {selectedThread.question && (
                              <Chip
                                size="small"
                                color="secondary"
                                variant="outlined"
                                label={`题目: ${selectedThread.question.questionNumber} - ${selectedThread.question.title}`}
                              />
                            )}
                            <Chip 
                              size="small" 
                              color={selectedThread.isResolved ? 'success' : 'default'}
                              variant={selectedThread.isResolved ? 'filled' : 'outlined'}
                              label={selectedThread.isResolved ? '已解决' : '未解决'}
                            />
                            {selectedThread.pinned && <Chip size="small" color="warning" label="置顶" icon={<PushPinIcon fontSize="small" />} />}
                            <Chip size="small" variant="outlined" label={`${selectedThread.replyCount} 回复`} />
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                            <UserAvatar 
                              user={{ name: selectedThread.authorName, authorAvatar: selectedThread.authorAvatar }}
                              size={28}
                            />
                            <Typography variant="body2">
                              {selectedThread.authorName} · {selectedThread.authorRole === 'teacher' ? '教师' : '学生'}
                            </Typography>
                            <Typography variant="caption">
                              创建于 {dayjs(selectedThread.createdAt).format('YYYY/MM/DD HH:mm')}
                            </Typography>
                          </Stack>
                          {selectedThread.question && selectedThread.question.templateCode && (
                            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                关联题目内容：
                              </Typography>
                              <Box
                                sx={{
                                  mt: 1,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                  '& pre': {
                                    margin: 0,
                                    borderRadius: 0,
                                  }
                                }}
                              >
                                <SyntaxHighlighter
                                  language="python"
                                  style={oneLight}
                                  customStyle={{
                                    margin: 0,
                                    padding: '16px',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.5,
                                    backgroundColor: '#fafafa',
                                    maxHeight: '400px',
                                  }}
                                  showLineNumbers={false}
                                  wrapLines={true}
                                  wrapLongLines={true}
                                >
                                  {selectedThread.question.templateCode}
                                </SyntaxHighlighter>
                              </Box>
                            </Paper>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          {user?.role === 'teacher' && (
                            <Button
                              size="small"
                              startIcon={selectedThread.pinned ? <PushPinIcon /> : <PushPinOutlined />}
                              onClick={handlePinThread}
                            >
                              {selectedThread.pinned ? '取消置顶' : '置顶'}
                            </Button>
                          )}
                          {(user?.role === 'teacher' || selectedThread.authorId === user?.id) && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={handleDeleteThread}
                              disabled={deleting}
                            >
                              {deleting ? '删除中…' : '删除'}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                      <Box sx={{ mt: 2 }}>
                        {renderContent(selectedThread.content)}
                      </Box>
                    </Box>

                    {/* Replies */}
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        回答
                      </Typography>
                      {!hasAcceptedReply && !hasTeacherReply && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'warning.50' }}>
                          <Typography variant="body2" color="text.secondary">
                            等待老师回答
                          </Typography>
                        </Paper>
                      )}
                      <Stack spacing={2}>
                        {selectedThreadReplies.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            还没有回答，做第一个回复吧～
                          </Typography>
                        ) : (
                          selectedThreadReplies.map((reply) => (
                            <Paper key={reply.id} variant="outlined" sx={{ p: 2 }}>
                              <Stack direction="row" spacing={2} alignItems="flex-start">
                                <UserAvatar 
                                  user={{ name: reply.authorName, authorAvatar: reply.authorAvatar }}
                                  size={36}
                                  sx={{ bgcolor: reply.authorRole === 'teacher' ? 'warning.main' : 'primary.main' }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {reply.authorName}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={reply.authorRole === 'teacher' ? '教师' : '学生'}
                                      color={reply.authorRole === 'teacher' ? 'warning' : 'default'}
                                    />
                                    {reply.acceptedByTeacher && (
                                      <Chip
                                        size="small"
                                        color="success"
                                        label="教师认可"
                                        icon={<CheckCircle fontSize="small" />}
                                      />
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      {dayjs(reply.createdAt).fromNow()}
                                    </Typography>
                                  </Stack>
                                  <Box sx={{ mt: 1 }}>
                                    {renderContent(reply.content)}
                                  </Box>
                                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                                    <Tooltip title={reply.likedByMe ? '取消点赞' : '点赞'}>
                                      <Button
                                        size="small"
                                        startIcon={reply.likedByMe ? <ThumbUp /> : <ThumbUpOffAlt />}
                                        onClick={() => handleToggleLike(reply)}
                                      >
                                        {reply.likes || 0}
                                      </Button>
                                    </Tooltip>
                                    {user?.role === 'teacher' && (
                                      <Button
                                        size="small"
                                        variant={reply.acceptedByTeacher ? 'contained' : 'outlined'}
                                        color="success"
                                        onClick={() => handleAcceptReply(reply)}
                                        startIcon={<CheckCircle />}
                                      >
                                        {reply.acceptedByTeacher ? '取消认可' : '认可该回答'}
                                      </Button>
                                    )}
                                    {(user?.role === 'teacher' || reply.authorId === user?.id) && (
                                      <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDeleteReply(reply)}
                                        disabled={deletingReplyId === reply.id}
                                      >
                                        {deletingReplyId === reply.id ? '删除中…' : '删除'}
                                      </Button>
                                    )}
                                  </Stack>

                                  {/* Comments */}
                                  <Box sx={{ mt: 1.5, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                                    {(reply.comments || []).length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        暂无评论
                                      </Typography>
                                    ) : (
                                      <Stack spacing={1}>
                                        {(reply.comments || []).map((comment) => (
                                          <Paper key={comment.id} variant="outlined" sx={{ p: 1.2, bgcolor: 'grey.50' }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                              <UserAvatar 
                                                user={{ name: comment.authorName, authorAvatar: comment.authorAvatar }}
                                                size={24}
                                                sx={{ bgcolor: comment.authorRole === 'teacher' ? 'warning.main' : 'primary.main' }}
                                              />
                                              <Typography variant="body2" fontWeight="bold">
                                                {comment.authorName}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {dayjs(comment.createdAt).fromNow()}
                                              </Typography>
                                            </Stack>
                                            <Box sx={{ mt: 0.5 }}>
                                              {renderContent(comment.content)}
                                            </Box>
                                          </Paper>
                                        ))}
                                      </Stack>
                                    )}

                                    {/* Comment box */}
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="写下评论"
                                        value={commentInputs[reply.id] || ''}
                                        onChange={(e) =>
                                          setCommentInputs((prev) => ({ ...prev, [reply.id]: e.target.value }))
                                        }
                                      />
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleAddComment(reply)}
                                        disabled={!commentInputs[reply.id]?.trim()}
                                      >
                                        评论
                                      </Button>
                                    </Stack>
                                  </Box>
                                </Box>
                              </Stack>
                            </Paper>
                          ))
                        )}
                      </Stack>

                      {/* Reply box */}
                      {selectedThread && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Stack spacing={1.5}>
                            <TextField
                              label="写下你的回答"
                              multiline
                              minRows={3}
                              value={replyContent}
                              onChange={(e) => setReplyContent(e.target.value)}
                              fullWidth
                            />
                            <Box display="flex" justifyContent="flex-end">
                              <Button
                                variant="contained"
                                startIcon={<Send />}
                                onClick={handleAddReply}
                                disabled={postingReply || !replyContent.trim()}
                              >
                                {postingReply ? '发送中...' : '提交回答'}
                              </Button>
                            </Box>
                          </Stack>
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ForumPage;

